import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { setupStatusQueryKey } from '@/lib/setup-status'
import { API_BASE } from '@/lib/api-base'
import {
  CSRF_HEADER_NAME,
  clearLegacyTokenStorage,
  isUnsafeHttpMethod,
  readCsrfTokenFromDocument,
} from '@/lib/session-cookies'
interface Organization {
  id: string
  nome: string
  cnpj: string | null
  status: string
}

interface Tenant {
  id: string
  nome: string
  tipo_producao: string
  organization_id: string | null
  organization_nome: string | null
  role_org?: string | null
}

export interface User {
  id: string
  email: string
  nome: string
  role: string
  granja_id: string | null
  telegram_id?: number | null
}

type MeResponse = {
  id: string
  email: string
  nome: string
  role: string
  telegram_id?: number | null
  last_active_granja_id?: string | null
  granja_id?: string | null
}

interface AuthContextType {
  user: User | null
  login: () => Promise<boolean>
  logout: (expired?: boolean) => Promise<void>
  isLoading: boolean
  isAuthenticated: boolean
  organizations: Organization[]
  selectedOrgId: string | null
  setSelectedOrgId: (id: string | null) => void
  tenants: Tenant[]
  selectedTenantId: string | null
  setSelectedTenantId: (id: string | null) => void
  activeGranjaId: string | null
  isSuperAdmin: boolean
  apiHeaders: (extra?: HeadersInit) => HeadersInit
  apiFetch: (url: string, init?: RequestInit) => Promise<Response>
  showTenantBanner: boolean
  setShowTenantBanner: (v: boolean) => void
  refetchTenants: () => Promise<unknown>
  refetchOrganizations: () => Promise<unknown>
  refetchUser: () => Promise<User | null>
  switchGranja: (granjaId: string) => Promise<boolean>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

function mapMeToUser(data: MeResponse): User {
  const granjaId = data.granja_id ?? data.last_active_granja_id ?? null
  return {
    id: data.id,
    email: data.email,
    nome: data.nome,
    role: data.role,
    granja_id: granjaId,
    telegram_id: data.telegram_id ?? null,
  }
}

export function createSessionApiFetch(deps: {
  apiHeaders: (extra?: HeadersInit) => HeadersInit
  onUnauthorized: () => void
  onTenantRequired: () => void
}): (url: string, init?: RequestInit) => Promise<Response> {
  return async (url: string, init?: RequestInit) => {
    const headers = new Headers(deps.apiHeaders())
    const extra = init?.headers
    if (extra) {
      new Headers(extra).forEach((v, k) => headers.set(k, v))
    }
    const method = init?.method ?? 'GET'
    if (isUnsafeHttpMethod(method)) {
      const csrf = readCsrfTokenFromDocument()
      if (csrf) headers.set(CSRF_HEADER_NAME, csrf)
    }
    const res = await fetch(url, {
      ...init,
      method,
      headers,
      credentials: 'include',
    })
    if (res.status === 401) {
      deps.onUnauthorized()
      return res
    }
    if (res.status === 400) {
      try {
        const body = (await res.clone().json()) as { detail?: string; code?: string }
        const isTenantRequired =
          body?.code === 'TENANT_REQUIRED' ||
          (typeof body?.detail === 'string' && body.detail.includes('Selecione uma granja'))
        if (isTenantRequired) deps.onTenantRequired()
      } catch {
        /* ignore non-JSON */
      }
    }
    return res
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null)
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null)
  const [showTenantBanner, setShowTenantBanner] = useState(false)
  const userRef = useRef<User | null>(null)
  userRef.current = user

  const isSuperAdmin = user?.role === 'super_admin'
  const isAuthenticated = user !== null
  const activeGranjaId = isSuperAdmin ? selectedTenantId : (user?.granja_id ?? selectedTenantId)

  const clearSessionState = useCallback(
    (expired = false) => {
      if (expired) {
        sessionStorage.setItem('auth_error', 'Sua sessão expirou. Faça login novamente.')
      }
      setUser(null)
      setSelectedOrgId(null)
      setSelectedTenantId(null)
      setShowTenantBanner(false)
      queryClient.clear()
    },
    [queryClient],
  )

  const apiHeaders = useCallback(
    (extra?: HeadersInit): HeadersInit => {
      const h: Record<string, string> = {}
      if (isSuperAdmin && selectedTenantId) {
        h['X-Tenant-ID'] = selectedTenantId
      }
      if (extra && typeof extra === 'object' && !Array.isArray(extra)) {
        Object.assign(h, extra)
      }
      return h
    },
    [isSuperAdmin, selectedTenantId],
  )

  const logout = useCallback(
    async (expired = false) => {
      try {
        await fetch(`${API_BASE}/api/auth/logout`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            ...Object.fromEntries(new Headers(apiHeaders())),
            ...(readCsrfTokenFromDocument()
              ? { [CSRF_HEADER_NAME]: readCsrfTokenFromDocument() as string }
              : {}),
          },
        })
      } catch {
        /* best effort */
      }
      clearLegacyTokenStorage()
      clearSessionState(expired)
    },
    [apiHeaders, clearSessionState],
  )

  const apiFetch = useMemo(
    () =>
      createSessionApiFetch({
        apiHeaders,
        onUnauthorized: () => {
          if (userRef.current) void logout(true)
        },
        onTenantRequired: () => setShowTenantBanner(true),
      }),
    [apiHeaders, logout],
  )

  const refetchUser = useCallback(async (): Promise<User | null> => {
    const res = await apiFetch(`${API_BASE}/api/auth/me`)
    if (res.status === 401) {
      setUser(null)
      return null
    }
    if (!res.ok) return null
    const userData = mapMeToUser((await res.json()) as MeResponse)
    setUser(userData)
    if (userData.granja_id && userData.role !== 'super_admin') {
      setSelectedTenantId(userData.granja_id)
    }
    return userData
  }, [apiFetch])

  const login = useCallback(async (): Promise<boolean> => {
    const userData = await refetchUser()
    return userData !== null
  }, [refetchUser])

  const { data: organizations = [], refetch: refetchOrganizations } = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      if (!user || !isSuperAdmin) return []
      const res = await apiFetch(`${API_BASE}/api/admin/organizations`)
      if (res.status === 401) return []
      if (!res.ok) return []
      return res.json()
    },
    enabled: !!user && isSuperAdmin,
  })

  const { data: allTenants = [], refetch: refetchTenants } = useQuery({
    queryKey: isSuperAdmin ? ['tenants'] : ['granjas'],
    queryFn: async () => {
      if (!user) return []
      const url = isSuperAdmin
        ? `${API_BASE}/api/admin/tenants?limit=200`
        : `${API_BASE}/api/auth/me/granjas`
      const res = await apiFetch(url)
      if (res.status === 401) return []
      if (!res.ok) return []
      const data = await res.json()
      if (isSuperAdmin) {
        return Array.isArray(data) ? data : (data.items ?? [])
      }
      const granjas = Array.isArray(data) ? data : []
      return granjas.map(
        (g: {
          id: string
          nome: string
          tipo_producao: string
          organization_id?: string | null
          organization_nome?: string | null
          role_org?: string | null
        }) => ({
          id: g.id,
          nome: g.nome,
          tipo_producao: g.tipo_producao,
          organization_id: g.organization_id ?? null,
          organization_nome: g.organization_nome ?? null,
          role_org: g.role_org ?? null,
        }),
      )
    },
    enabled: !!user,
  })

  const tenants = useMemo(() => {
    if (isSuperAdmin && selectedOrgId) {
      return allTenants.filter((t: Tenant) => t.organization_id === selectedOrgId)
    }
    return allTenants
  }, [isSuperAdmin, selectedOrgId, allTenants])

  const switchGranja = useCallback(
    async (granjaId: string): Promise<boolean> => {
      if (!user) return false
      const res = await apiFetch(`${API_BASE}/api/auth/me/switch-granja`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ granja_id: granjaId }),
      })
      if (res.status === 401) {
        await logout(true)
        return false
      }
      if (!res.ok) return false
      setSelectedTenantId(granjaId)
      await refetchUser()
      await queryClient.invalidateQueries({ queryKey: setupStatusQueryKey(granjaId) })
      return true
    },
    [user, apiFetch, logout, refetchUser, queryClient],
  )

  useEffect(() => {
    clearLegacyTokenStorage()
    void (async () => {
      try {
        await refetchUser()
      } finally {
        setIsLoading(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bootstrap once on mount
  }, [])

  useEffect(() => {
    if (isSuperAdmin && !selectedOrgId && organizations.length > 0) {
      setSelectedOrgId(organizations[0].id)
    }
  }, [isSuperAdmin, selectedOrgId, organizations])

  useEffect(() => {
    if (isSuperAdmin && selectedOrgId) {
      const orgTenants = allTenants.filter((t: Tenant) => t.organization_id === selectedOrgId)
      const currentInOrg = orgTenants.some((t: Tenant) => t.id === selectedTenantId)
      if (!currentInOrg && orgTenants.length > 0) {
        setSelectedTenantId(orgTenants[0].id)
      } else if (orgTenants.length === 0) {
        setSelectedTenantId(null)
      }
    }
  }, [selectedOrgId, allTenants, isSuperAdmin, selectedTenantId])

  useEffect(() => {
    if (tenants.length === 1 && !selectedTenantId && !isSuperAdmin) {
      setSelectedTenantId(tenants[0].id)
    }
  }, [tenants, selectedTenantId, isSuperAdmin])

  useEffect(() => {
    if (selectedTenantId) setShowTenantBanner(false)
  }, [selectedTenantId])

  const value: AuthContextType = {
    user,
    login,
    logout,
    isLoading,
    isAuthenticated,
    organizations,
    selectedOrgId,
    setSelectedOrgId,
    tenants,
    selectedTenantId,
    setSelectedTenantId,
    activeGranjaId,
    isSuperAdmin,
    apiHeaders,
    apiFetch,
    showTenantBanner,
    setShowTenantBanner,
    refetchTenants,
    refetchOrganizations,
    refetchUser,
    switchGranja,
  }

  return (
    <AuthContext.Provider value={value}>
      <TenantBanner />
      {children}
    </AuthContext.Provider>
  )
}

function TenantBanner() {
  const { showTenantBanner, setShowTenantBanner, isSuperAdmin } = useAuth()
  if (!showTenantBanner || !isSuperAdmin) return null
  return (
    <div
      role="alert"
      className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-amber-950 px-4 py-2 flex items-center justify-between gap-4 shadow"
    >
      <span className="text-sm font-medium">Selecione uma granja no menu acima para continuar.</span>
      <button
        type="button"
        onClick={() => setShowTenantBanner(false)}
        className="text-sm underline hover:no-underline"
      >
        Fechar
      </button>
    </div>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
